/**
 * VaultSearchView — the workspace's Search: free, instant filtering by name,
 * type, area, size and date, shown three ways (Standard, By Type, By Arche),
 * modelled on the email mailbox's By Sender.
 *
 * Two kinds of search, never confused:
 *   - Filtering (the default) — metadata only, free, runs as you type.
 *   - "Search inside files" — the billed semantic search, a separate,
 *     labelled action that typing in the filter box never triggers.
 *
 * All three areas are searched by default (nothing the user pays for is out
 * of sight); every result says where it lives. The list, its header and the
 * By Type / By Arche cards come from one server-side WHERE, so their numbers
 * agree — and they count only files this person's role may see (every file,
 * for an owner or admin; a member or viewer is told when some are above them).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Search as SearchIcon,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import type { VaultDocumentDto } from '@/lib/api';
import type { HolderSwitcherOption } from '@/components/HolderSwitcher';
import { cn } from '@/lib/utils';
import {
  useVaultDocumentFolders,
  useVaultDocumentGroups,
  useVaultDocumentsList,
  useVaultDocumentsSearch,
} from '../hooks/useVaultDocuments';
import { useFileActions } from '../hooks/useFileActions';
import { useDocumentPlacement } from '../components/DocumentPlacement';
import { DocumentsView, ViewModeToggle } from '../components/shared/DocumentsView';
import { useOpenFile } from '../hooks/useOpenFile';
import { SemanticSearchResults } from '../components/shared/SemanticSearchResults';
import { SearchFilterBar } from '../components/search/SearchFilterBar';
import { GroupsView, archeCards, kindCards } from '../components/search/GroupsView';
import {
  EMPTY_SEARCH_FILTERS,
  describeTotals,
  matchesNothing,
  searchFiltersToQuery,
  type SearchDrill,
  type SearchFilterState,
} from '../components/search/search-filters';
import { describeFolderLocation } from '../lib/locations';
import { usePersistedState } from '../lib/persisted-state';
import { ARCHE_ID } from '../constants';

type GroupBy = 'standard' | 'kind' | 'arche';
type ListSort = { sort: 'updated' | 'name' | 'size' | 'kind'; direction: 'asc' | 'desc' };

const GROUP_BY_TABS: Array<{ value: GroupBy; label: string }> = [
  { value: 'standard', label: 'Standard' },
  { value: 'kind', label: 'By Type' },
  { value: 'arche', label: 'By Arche' },
];
const SORTS: Array<{ value: ListSort['sort']; label: string; firstDirection: 'asc' | 'desc' }> = [
  { value: 'updated', label: 'Modified', firstDirection: 'desc' },
  { value: 'name', label: 'Name', firstDirection: 'asc' },
  { value: 'size', label: 'Size', firstDirection: 'desc' },
  { value: 'kind', label: 'Type', firstDirection: 'asc' },
];
/** Filtering waits this long after the last keystroke. */
const TEXT_DEBOUNCE_MS = 250;

const isGroupBy = (v: unknown): v is GroupBy => v === 'standard' || v === 'kind' || v === 'arche';
const isListSort = (v: unknown): v is ListSort =>
  !!v &&
  typeof v === 'object' &&
  SORTS.some((s) => s.value === (v as ListSort).sort) &&
  ((v as ListSort).direction === 'asc' || (v as ListSort).direction === 'desc');
const isViewMode = (v: unknown): v is 'list' | 'grid' => v === 'list' || v === 'grid';

export function VaultSearchView({
  holderId,
  holder,
}: {
  holderId: string;
  holder: HolderSwitcherOption | null | undefined;
}) {
  const navigate = useNavigate();
  const folders = useVaultDocumentFolders(holderId);
  const placement = useDocumentPlacement({ folders: folders.data });
  const fileActions = useFileActions({ holderId, holder, folders: folders.data, placement });

  const [mode, setMode] = useState<'filter' | 'semantic'>('filter');
  const [state, setState] = useState<SearchFilterState>(EMPTY_SEARCH_FILTERS);
  const [textInput, setTextInput] = useState('');
  const [semanticInput, setSemanticInput] = useState('');
  const [semanticQuery, setSemanticQuery] = useState('');
  const [drill, setDrill] = useState<SearchDrill | null>(null);
  const opener = useOpenFile();
  const [groupBy, setGroupBy] = usePersistedState<GroupBy>(
    'fias.vault.search.groupBy',
    'standard',
    isGroupBy,
  );
  const [listSort, setListSort] = usePersistedState<ListSort>(
    'fias.vault.search.sort',
    { sort: 'updated', direction: 'desc' },
    isListSort,
  );
  const [viewMode, setViewMode] = usePersistedState<'list' | 'grid'>(
    'fias.vault.search.viewMode',
    'list',
    isViewMode,
  );

  // Typing filters after a pause; a chip that clears the name clears the box.
  useEffect(() => {
    const timer = setTimeout(
      () => setState((s) => (s.text === textInput ? s : { ...s, text: textInput })),
      TEXT_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [textInput]);
  useEffect(() => {
    if (state.text === '') setTextInput((current) => (current.trim() === '' ? current : ''));
  }, [state.text]);

  const nothing = matchesNothing(state, drill);
  const query = useMemo(() => searchFiltersToQuery(state, drill), [state, drill]);
  const showList = groupBy === 'standard' || drill !== null;

  const totals = useVaultDocumentGroups(holderId, 'kind', query, {
    enabled: mode === 'filter' && !nothing,
  });
  const byArche = useVaultDocumentGroups(holderId, 'arche', query, {
    enabled: mode === 'filter' && !nothing && groupBy === 'arche' && drill === null,
  });
  const list = useVaultDocumentsList(mode === 'filter' && showList && !nothing ? holderId : null, {
    ...query,
    sort: listSort.sort,
    direction: listSort.direction,
  });
  const semantic = useVaultDocumentsSearch(
    holderId,
    { query: semanticQuery, topK: 20 },
    { enabled: mode === 'semantic' && !!semanticQuery },
  );

  const folderList = folders.data ?? [];
  const locationOf = (doc: VaultDocumentDto) => describeFolderLocation(doc.folderId, folderList);

  // Counts cover what this person's role may see. Only a member or viewer of
  // a shared workspace sees less than everything (the sensitivity tiers above
  // their role), so only they are told; an owner or admin sees it all.
  const roleHidesFiles = holder?.role === 'member' || holder?.role === 'viewer';
  const header = nothing
    ? 'No areas chosen — pick at least one area to search.'
    : totals.data
      ? `${describeTotals(totals.data.total.count, totals.data.total.totalBytes)}${
          roleHidesFiles ? ' — files above your access level aren’t counted' : ''
        }`
      : totals.isError
        ? 'Couldn’t count the matching files.'
        : 'Counting…';

  const chooseSort = (value: ListSort['sort']) => {
    const spec = SORTS.find((s) => s.value === value)!;
    setListSort(
      listSort.sort === value
        ? { sort: value, direction: listSort.direction === 'asc' ? 'desc' : 'asc' }
        : { sort: value, direction: spec.firstDirection },
    );
  };

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      {placement.ui}
      {fileActions.ui}

      <div className="flex flex-wrap items-center gap-2">
        <div
          role="tablist"
          aria-label="Kind of search"
          className="flex items-center rounded-md border border-border bg-card p-0.5 text-sm"
        >
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'filter'}
            onClick={() => setMode('filter')}
            className={cn(
              'flex items-center gap-1.5 rounded px-3 py-1.5',
              mode === 'filter'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <SlidersHorizontal className="h-4 w-4" aria-hidden /> Filter
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'semantic'}
            onClick={() => setMode('semantic')}
            className={cn(
              'flex items-center gap-1.5 rounded px-3 py-1.5',
              mode === 'semantic'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <SearchIcon className="h-4 w-4" aria-hidden /> Search inside files
          </button>
        </div>

        {mode === 'filter' ? (
          <div className="relative min-w-[12rem] flex-1">
            <input
              type="search"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              placeholder="Filter by name"
              aria-label="Filter by name"
              maxLength={120}
              className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm"
            />
          </div>
        ) : (
          <form
            className="flex min-w-[12rem] flex-1 items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setSemanticQuery(semanticInput.trim());
            }}
          >
            <input
              type="text"
              value={semanticInput}
              onChange={(e) => setSemanticInput(e.target.value)}
              placeholder="What’s inside? e.g. ‘renewal terms’, ‘NDA scope’"
              aria-label="Search inside files"
              maxLength={2000}
              className="min-w-0 flex-1 rounded-md border border-border bg-card px-3 py-2 text-sm"
            />
            <button
              type="submit"
              disabled={!semanticInput.trim()}
              className="shrink-0 rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground disabled:opacity-50"
            >
              Search (uses credits)
            </button>
          </form>
        )}
      </div>

      {mode === 'semantic' ? (
        <div className="flex flex-col gap-2">
          <p className="text-xs text-muted-foreground">
            Searches the text inside your files, which uses credits each time you search. To find
            files by name, type, size or date for free, use Filter.
          </p>
          {semanticQuery && (
            <SemanticSearchResults
              query={semanticQuery}
              loading={semantic.isLoading}
              matches={semantic.data}
              onOpen={(documentId) => navigate(`/a/${ARCHE_ID}/${documentId}`)}
            />
          )}
        </div>
      ) : (
        <>
          <SearchFilterBar
            state={state}
            onChange={(next) => {
              setState(next);
              if (next.text !== state.text) setTextInput(next.text);
            }}
          />

          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-2">
            <p className="text-sm text-muted-foreground" aria-live="polite">
              {header}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <div
                role="tablist"
                aria-label="Show results"
                className="flex items-center rounded-md border border-border bg-card p-0.5 text-xs"
              >
                {GROUP_BY_TABS.map((tab) => (
                  <button
                    key={tab.value}
                    type="button"
                    role="tab"
                    aria-selected={groupBy === tab.value && !drill}
                    onClick={() => {
                      setGroupBy(tab.value);
                      setDrill(null);
                    }}
                    className={cn(
                      'rounded px-2.5 py-1',
                      groupBy === tab.value && !drill
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              {showList && <ViewModeToggle mode={viewMode} onChange={setViewMode} />}
            </div>
          </div>

          {drill && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <button
                type="button"
                onClick={() => setDrill(null)}
                className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-accent"
              >
                <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
                {drill.by === 'kind' ? 'By Type' : 'By Arche'}
              </button>
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs">
                {drill.by === 'kind' ? 'Type' : 'Made by'}: {drill.label}
                <button
                  type="button"
                  aria-label={`Stop showing only ${drill.label}`}
                  onClick={() => setDrill(null)}
                  className="rounded-full p-0.5 text-muted-foreground hover:bg-background"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            </div>
          )}

          {nothing ? null : showList ? (
            <>
              <div className="flex flex-wrap items-center gap-1.5" aria-label="Sort results">
                <span className="text-xs text-muted-foreground">Sort</span>
                {SORTS.map((s) => {
                  const active = listSort.sort === s.value;
                  return (
                    <button
                      key={s.value}
                      type="button"
                      aria-pressed={active}
                      onClick={() => chooseSort(s.value)}
                      className={cn(
                        'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs',
                        active
                          ? 'border-primary bg-primary/10 text-foreground'
                          : 'border-border text-muted-foreground hover:bg-accent',
                      )}
                    >
                      {s.label}
                      {active &&
                        (listSort.direction === 'asc' ? (
                          <ArrowUp className="h-3 w-3" aria-label="ascending" />
                        ) : (
                          <ArrowDown className="h-3 w-3" aria-label="descending" />
                        ))}
                    </button>
                  );
                })}
              </div>
              <DocumentsView
                mode={viewMode}
                loading={list.isLoading}
                documents={list.data ?? []}
                folders={folderList}
                currentFolderId={null}
                showFolders={false}
                isTrash={false}
                onOpenDocument={opener.open}
                onOpenFolder={() => {}}
                actionsFor={fileActions.actionsFor}
                locationOf={locationOf}
                emptyMessage="No files match these filters."
              />
              {list.hasNextPage && (
                <div className="flex items-center justify-center gap-3 text-xs text-muted-foreground">
                  <span>
                    Showing {(list.data ?? []).length} of {list.total}
                  </span>
                  <button
                    type="button"
                    className="rounded border border-border px-3 py-1 text-sm text-foreground hover:bg-accent disabled:opacity-50"
                    disabled={list.isFetchingNextPage}
                    onClick={() => void list.fetchNextPage()}
                  >
                    {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
                  </button>
                </div>
              )}
            </>
          ) : groupBy === 'kind' ? (
            <GroupsView
              cards={totals.data ? kindCards(totals.data) : []}
              loading={totals.isLoading}
              error={totals.isError}
              onOpen={setDrill}
              emptyMessage="No files match these filters."
            />
          ) : (
            <GroupsView
              cards={byArche.data ? archeCards(byArche.data) : []}
              loading={byArche.isLoading}
              error={byArche.isError}
              other={byArche.data?.other}
              onOpen={setDrill}
              emptyMessage="No files match these filters."
            />
          )}
        </>
      )}

      {opener.ui}
    </div>
  );
}
